import { useState } from 'react';
import {
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  ViewColumnsIcon,
  CpuChipIcon,
  CommandLineIcon,
  DocumentTextIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface DashboardLayout {
  id: string;
  name: string;
  description: string;
  selected: boolean;
}

interface ModelSetting {
  id: string;
  model: string;
  setting: string;
  description: string;
  enabled: boolean;
}

interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

interface RateLimitSetting {
  id: string;
  setting: string;
  description: string;
  enabled: boolean;
}

const mockLayouts: DashboardLayout[] = [
  {
    id: '1',
    name: 'Default',
    description: 'Standard dashboard layout with all widgets',
    selected: true,
  },
  {
    id: '2',
    name: 'Compact',
    description: 'Condensed view with essential metrics only',
    selected: false,
  },
  {
    id: '3',
    name: 'Expanded',
    description: 'Detailed view with larger widgets and more information',
    selected: false,
  },
];

const mockModelSettings: ModelSetting[] = [
  {
    id: '1',
    model: 'GPT-4',
    setting: 'Enable Context Window Extension',
    description: 'Automatically split and process longer inputs',
    enabled: true,
  },
  {
    id: '2',
    model: 'GPT-3.5 Turbo',
    setting: 'Auto-Fallback',
    description: 'Fallback to GPT-3.5 when GPT-4 quota is exceeded',
    enabled: true,
  },
  {
    id: '3',
    model: 'Claude 2',
    setting: 'Enable Tool Use',
    description: 'Allow model to use external tools and APIs',
    enabled: false,
  },
];

const mockPromptTemplates: PromptTemplate[] = [
  {
    id: '1',
    name: 'Auto-Retry Failed Completions',
    description: 'Automatically retry failed API calls with backoff',
    enabled: true,
  },
  {
    id: '2',
    name: 'Token Optimization',
    description: 'Optimize prompts to reduce token usage',
    enabled: true,
  },
  {
    id: '3',
    name: 'Context Preservation',
    description: 'Maintain conversation context across requests',
    enabled: false,
  },
];

const mockRateLimits: RateLimitSetting[] = [
  {
    id: '1',
    setting: 'Request Rate Limiting',
    description: 'Limit requests per minute per API key',
    enabled: true,
  },
  {
    id: '2',
    setting: 'Token Usage Throttling',
    description: 'Gradually reduce rate as token limit approaches',
    enabled: true,
  },
  {
    id: '3',
    setting: 'Cost-Based Throttling',
    description: 'Adjust request rate based on cost thresholds',
    enabled: false,
  },
];

export default function Settings() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [layouts, setLayouts] = useState(mockLayouts);
  const [modelSettings, setModelSettings] = useState(mockModelSettings);
  const [promptTemplates, setPromptTemplates] = useState(mockPromptTemplates);
  const [rateLimits, setRateLimits] = useState(mockRateLimits);
  const [responseSettings, setResponseSettings] = useState({
    streamResponses: true,
    enableMarkdown: true,
    syntaxHighlighting: true,
    formatJSON: true,
  });

  const selectLayout = (id: string) => {
    setLayouts(layouts.map(layout => ({
      ...layout,
      selected: layout.id === id,
    })));
  };

  const toggleModelSetting = (id: string) => {
    setModelSettings(settings => settings.map(setting =>
      setting.id === id ? { ...setting, enabled: !setting.enabled } : setting
    ));
  };

  const togglePromptTemplate = (id: string) => {
    setPromptTemplates(templates => templates.map(template =>
      template.id === id ? { ...template, enabled: !template.enabled } : template
    ));
  };

  const toggleRateLimit = (id: string) => {
    setRateLimits(limits => limits.map(limit =>
      limit.id === id ? { ...limit, enabled: !limit.enabled } : limit
    ));
  };

  return (
    <div className="space-y-8">
      {/* Settings Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <Cog6ToothIcon className="h-6 w-6 text-white/90" />
          <h1 className="text-xl font-medium text-white">Settings</h1>
        </div>

        {/* Theme Settings */}
        <div className="p-8">
          <div className="flex items-center justify-between p-4 glass-panel-dark">
            <div>
              <div className="font-medium text-white">Theme Preference</div>
              <div className="text-sm text-white/50">
                Choose between light and dark mode
              </div>
            </div>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="btn-secondary flex items-center space-x-2"
            >
              {isDarkMode ? (
                <>
                  <SunIcon className="h-5 w-5" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <MoonIcon className="h-5 w-5" />
                  <span>Dark Mode</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Model Settings */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <CpuChipIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Model Settings</h2>
        </div>
        <div className="p-8 space-y-4">
          {modelSettings.map((setting) => (
            <div
              key={setting.id}
              className="flex items-center justify-between p-4 glass-panel-dark"
            >
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-white">{setting.setting}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.05] text-white/70">
                    {setting.model}
                  </span>
                </div>
                <div className="text-sm text-white/50">{setting.description}</div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => toggleModelSetting(setting.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    setting.enabled ? 'bg-magick-purple' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      setting.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prompt Templates */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <CommandLineIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Prompt Management</h2>
        </div>
        <div className="p-8 space-y-4">
          {promptTemplates.map((template) => (
            <div
              key={template.id}
              className="flex items-center justify-between p-4 glass-panel-dark"
            >
              <div>
                <div className="font-medium text-white">{template.name}</div>
                <div className="text-sm text-white/50">{template.description}</div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => togglePromptTemplate(template.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    template.enabled ? 'bg-magick-purple' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      template.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Response Settings */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <DocumentTextIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Response Settings</h2>
        </div>
        <div className="p-8 space-y-4">
          <div className="flex items-center justify-between p-4 glass-panel-dark">
            <div>
              <div className="font-medium text-white">Stream Responses</div>
              <div className="text-sm text-white/50">
                Show responses as they are generated
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setResponseSettings(prev => ({
                  ...prev,
                  streamResponses: !prev.streamResponses,
                }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  responseSettings.streamResponses ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    responseSettings.streamResponses ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 glass-panel-dark">
            <div>
              <div className="font-medium text-white">Markdown Formatting</div>
              <div className="text-sm text-white/50">
                Enable markdown rendering in responses
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setResponseSettings(prev => ({
                  ...prev,
                  enableMarkdown: !prev.enableMarkdown,
                }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  responseSettings.enableMarkdown ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    responseSettings.enableMarkdown ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 glass-panel-dark">
            <div>
              <div className="font-medium text-white">Syntax Highlighting</div>
              <div className="text-sm text-white/50">
                Enable syntax highlighting for code blocks
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setResponseSettings(prev => ({
                  ...prev,
                  syntaxHighlighting: !prev.syntaxHighlighting,
                }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  responseSettings.syntaxHighlighting ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    responseSettings.syntaxHighlighting ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 glass-panel-dark">
            <div>
              <div className="font-medium text-white">Format JSON</div>
              <div className="text-sm text-white/50">
                Automatically format JSON responses
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setResponseSettings(prev => ({
                  ...prev,
                  formatJSON: !prev.formatJSON,
                }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  responseSettings.formatJSON ? 'bg-magick-purple' : 'bg-white/10'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    responseSettings.formatJSON ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rate Limiting */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <ClockIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Rate Limiting</h2>
        </div>
        <div className="p-8 space-y-4">
          {rateLimits.map((limit) => (
            <div
              key={limit.id}
              className="flex items-center justify-between p-4 glass-panel-dark"
            >
              <div>
                <div className="font-medium text-white">{limit.setting}</div>
                <div className="text-sm text-white/50">{limit.description}</div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => toggleRateLimit(limit.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    limit.enabled ? 'bg-magick-purple' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      limit.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dashboard Layout */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <ViewColumnsIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Dashboard Layout</h2>
        </div>
        <div className="p-8 grid grid-cols-3 gap-6">
          {layouts.map((layout) => (
            <div
              key={layout.id}
              className={`glass-panel-dark p-6 cursor-pointer transition-colors duration-200 ${
                layout.selected ? 'ring-2 ring-magick-purple' : ''
              }`}
              onClick={() => selectLayout(layout.id)}
            >
              <div className="font-medium text-white">{layout.name}</div>
              <div className="mt-1 text-sm text-white/50">
                {layout.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
